import { useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  MessageSquare, 
  Database, 
  Code, 
  Users, 
  TrendingUp,
  ChevronRight,
  Wand2,
  BarChart3,
  Lightbulb,
  Info
} from 'lucide-react';
import { useSimulationLimits } from '@/hooks/useSimulationLimits';
import { InterviewChat } from './chat/InterviewChat';
import { InterviewType, InterviewMode, InterviewDifficulty } from '@/hooks/useInterviewSimulation';
import { PaywallDialog } from '@/components/practice/PaywallDialog';

const interviewTypes = [
  {
    id: 'technical' as InterviewType,
    title: 'Entrevista Técnica',
    description: 'SQL, Python, e conceitos de dados',
    icon: Database,
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
    tip: 'A IA fará perguntas progressivas sobre SQL, Python ou Power BI, avaliando seu raciocínio técnico e capacidade de resolver problemas reais.',
    categories: [
      { id: 'sql', label: 'SQL', icon: Database, tip: 'Você será avaliado em consultas, joins, agregações, subqueries, window functions e modelagem de dados.' },
      { id: 'python', label: 'Python', icon: Code, tip: 'Espere perguntas sobre pandas, manipulação de dados, lógica de programação e boas práticas de código.' },
      { id: 'powerbi', label: 'Power BI', icon: BarChart3, tip: 'A IA perguntará sobre DAX, modelagem dimensional, criação de dashboards e boas práticas de visualização.' },
    ]
  },
  {
    id: 'behavioral' as InterviewType,
    title: 'Entrevista Comportamental',
    description: 'Soft skills e método STAR',
    icon: Users,
    color: 'text-purple-500',
    bgColor: 'bg-purple-500/10',
    tip: 'A IA usará o método STAR (Situação, Tarefa, Ação, Resultado) para avaliar suas soft skills, liderança e resolução de conflitos.',
    categories: [
      { id: 'default', label: 'Geral', icon: Users, tip: 'Prepare exemplos reais da sua carreira. A IA vai avaliar clareza, estrutura e impacto das suas respostas.' },
    ]
  },
  {
    id: 'business_case' as InterviewType,
    title: 'Case de Negócio',
    description: 'Análise de problemas reais',
    icon: TrendingUp,
    color: 'text-green-500',
    bgColor: 'bg-green-500/10',
    tip: 'Você receberá um cenário de negócio real e precisará propor soluções baseadas em dados, demonstrando pensamento analítico.',
    categories: [
      { id: 'default', label: 'Case Geral', icon: TrendingUp, tip: 'Foque em estruturar seu raciocínio: entenda o problema, proponha métricas, analise dados e apresente recomendações.' },
    ]
  },
];

const difficulties = [
  { id: 'junior' as InterviewDifficulty, label: 'Júnior', description: '0-2 anos', tip: 'Perguntas sobre fundamentos e conceitos básicos. Ideal para quem está começando na área.' },
  { id: 'pleno' as InterviewDifficulty, label: 'Pleno', description: '2-5 anos', tip: 'Cenários mais complexos que exigem experiência prática e capacidade de resolver problemas reais.' },
  { id: 'senior' as InterviewDifficulty, label: 'Sênior', description: '5+ anos', tip: 'Perguntas sobre arquitetura, otimização, liderança técnica e decisões estratégicas.' },
];

type Step = 'type' | 'category' | 'difficulty' | 'simulation';

export function InterviewHub() {
  const { canSimulate, remaining, isLoading: limitsLoading, refreshLimit } = useSimulationLimits();
  const [step, setStep] = useState<Step>('type');
  const [selectedType, setSelectedType] = useState<InterviewType | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedDifficulty, setSelectedDifficulty] = useState<InterviewDifficulty | null>(null);
  const [showPaywall, setShowPaywall] = useState(false);

  const handleTypeSelect = (type: InterviewType) => {
    setSelectedType(type);
    const typeConfig = interviewTypes.find(t => t.id === type);
    if (typeConfig?.categories.length === 1) {
      setSelectedCategory(typeConfig.categories[0].id);
      setStep('difficulty');
    } else {
      setStep('category');
    }
  };

  const handleCategorySelect = (category: string) => {
    setSelectedCategory(category);
    setStep('difficulty');
  };

  const handleDifficultySelect = (difficulty: InterviewDifficulty) => {
    if (!canSimulate) {
      setShowPaywall(true);
      return;
    }
    setSelectedDifficulty(difficulty);
    setStep('simulation');
  };

  const handleBack = () => {
    switch (step) {
      case 'category':
        setStep('type');
        setSelectedType(null);
        break;
      case 'difficulty':
        const typeConfig = interviewTypes.find(t => t.id === selectedType);
        if (typeConfig?.categories.length === 1) {
          setStep('type');
          setSelectedType(null);
          setSelectedCategory(null);
        } else {
          setStep('category');
          setSelectedCategory(null);
        }
        break;
      case 'simulation':
        setStep('difficulty');
        setSelectedDifficulty(null);
        break;
    }
  };

  const handleSimulationEnd = () => {
    setStep('type');
    setSelectedType(null);
    setSelectedCategory(null);
    setSelectedDifficulty(null);
    refreshLimit();
  };

  if (step === 'simulation' && selectedType && selectedDifficulty) {
    return (
      <InterviewChat
        type={selectedType}
        mode={'chat' as InterviewMode}
        category={selectedCategory || 'default'}
        difficulty={selectedDifficulty}
        onBack={handleBack}
        onEnd={handleSimulationEnd}
      />
    );
  }

  const selectedTypeConfig = interviewTypes.find(t => t.id === selectedType);
  const selectedCategoryConfig = selectedTypeConfig?.categories.find(c => c.id === selectedCategory);

  return (
    <div className="space-y-6">
      {/* Header with Limits */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Simulador de Entrevistas</h2>
          <p className="text-muted-foreground">
            Pratique entrevistas reais com IA e receba feedback detalhado
          </p>
        </div>
        {!limitsLoading && (
          <Badge variant={canSimulate ? "secondary" : "destructive"} className="text-sm py-1 px-3">
            <Wand2 className="h-3.5 w-3.5 mr-1.5" />
            {remaining > 100 ? 'Ilimitado' : `${remaining}/3 simulações restantes`}
          </Badge>
        )}
      </div>

      {/* Step Content */}
      {step === 'type' && (
        <>
          {/* Info card */}
          <Card className="border-blue-500/20 bg-blue-500/5">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-blue-500/10 shrink-0">
                  <Info className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Como funciona?</h3>
                  <p className="text-sm text-muted-foreground">
                    Escolha o tipo de entrevista e o nível de dificuldade. A IA vai simular um entrevistador 
                    real por <strong>chat</strong>, fazendo perguntas progressivas e avaliando suas respostas. 
                    Ao final, você recebe um feedback detalhado com pontos fortes e áreas de melhoria.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid gap-4 md:grid-cols-3">
            {interviewTypes.map((type) => (
              <Card 
                key={type.id}
                className="cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02]"
                onClick={() => handleTypeSelect(type.id)}
              >
                <CardHeader>
                  <div className={`w-12 h-12 rounded-lg ${type.bgColor} flex items-center justify-center mb-3`}>
                    <type.icon className={`h-6 w-6 ${type.color}`} />
                  </div>
                  <CardTitle className="text-lg">{type.title}</CardTitle>
                  <CardDescription>{type.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-start gap-2 text-xs text-muted-foreground">
                    <Lightbulb className="h-3.5 w-3.5 shrink-0 mt-0.5 text-amber-500" />
                    <span>{type.tip}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}

      {step === 'category' && selectedType && (
        <div className="space-y-4">
          <Button variant="ghost" size="sm" onClick={handleBack}>
            ← Voltar
          </Button>
          <h3 className="text-lg font-semibold">Escolha a categoria</h3>

          {/* Info card */}
          <Card className="border-amber-500/20 bg-amber-500/5">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <Lightbulb className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
                <p className="text-sm text-muted-foreground">
                  Cada categoria avalia habilidades específicas. Escolha a que mais se alinha com a vaga que você busca ou a área que deseja praticar.
                </p>
              </div>
            </CardContent>
          </Card>

          <div className="grid gap-4 md:grid-cols-2">
            {selectedTypeConfig?.categories.map((cat) => (
              <Card 
                key={cat.id}
                className="cursor-pointer hover:shadow-lg transition-all"
                onClick={() => handleCategorySelect(cat.id)}
              >
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <cat.icon className="h-5 w-5 text-primary" />
                    </div>
                    <p className="font-medium">{cat.label}</p>
                    <ChevronRight className="h-5 w-5 text-muted-foreground ml-auto" />
                  </div>
                  <p className="text-xs text-muted-foreground flex items-start gap-1.5">
                    <Lightbulb className="h-3.5 w-3.5 shrink-0 mt-0.5 text-amber-500" />
                    {cat.tip}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {step === 'difficulty' && (
        <div className="space-y-4">
          <Button variant="ghost" size="sm" onClick={handleBack}>
            ← Voltar
          </Button>
          <h3 className="text-lg font-semibold">Qual seu nível de experiência?</h3>

          {/* Info card */}
          <Card className="border-green-500/20 bg-green-500/5">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                <div className="text-sm text-muted-foreground space-y-1">
                  <p>O nível define a <strong>profundidade e complexidade</strong> das perguntas que a IA fará.</p>
                  {selectedCategoryConfig && (
                    <p className="text-xs italic">
                      Categoria selecionada: <strong>{selectedCategoryConfig.label}</strong> — {selectedCategoryConfig.tip}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid gap-4 md:grid-cols-3">
            {difficulties.map((diff) => (
              <Card 
                key={diff.id}
                className="cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02]"
                onClick={() => handleDifficultySelect(diff.id)}
              >
                <CardContent className="pt-6 text-center space-y-2">
                  <p className="font-medium text-lg">{diff.label}</p>
                  <p className="text-sm text-muted-foreground">{diff.description}</p>
                  <p className="text-xs text-muted-foreground flex items-start gap-1.5 text-left">
                    <Lightbulb className="h-3.5 w-3.5 shrink-0 mt-0.5 text-amber-500" />
                    {diff.tip}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {!canSimulate && (
            <Card className="bg-muted/50 border-dashed">
              <CardContent className="pt-6 text-center">
                <p className="text-muted-foreground">
                  Você atingiu o limite de simulações gratuitas deste mês.
                </p>
                <Button className="mt-3" onClick={() => setShowPaywall(true)}>
                  Desbloquear com Premium
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      <PaywallDialog 
        open={showPaywall} 
        onOpenChange={setShowPaywall}
        feature="simulações de entrevista ilimitadas"
      />
    </div>
  );
}
